<template lang="pug">
  .contact_item(
    :id="isNewGroup ? 'newGroupChat' : ''"
    @click="onClick",
    :class="{ active, 'special': chat.special, disabled, 'hasUnread': unreadMessages }")
    .contact_preview(v-if="chat.type_id === chatTypes.private && chat.contact")
      div(v-if="chat.contact && chat.contact.avatar_url", :style="{ 'background-image': 'url(' + avatarUrlWithResize(chat.contact.avatar_url, '50x50') + ')' }")
      AvatarPlaceholder.md(v-else, :fullName="chat.contact.first_name + ' ' + chat.contact.last_name", :classname="getAvatarClass(chat.contact.first_name, chat.contact.last_name)")
    .contact_preview(v-else)
      SvgMsgLogo.logo
    .contact_info
      v-layout(justify-space-between)
        .contact_info__name
          .name_wrap
            SvgMsgGroup(v-if="chat.type_id === chatTypes.group")
            SvgMsgLogo(v-if="chat.type_id === chatTypes.special")
            span {{ title }}
            SvgReferralProp.ref(v-if="chat.contact && chat.contact.ref_role", :referral="chat.contact.ref_role === 'referral'")
          .name_cancel(
            v-if="chat.cancellable",
            @click="cancel") {{ $t('actions.cancel') }}
        .contact_date(v-if="chat.last_message") {{ lastMessageDate }}
      .contact_info__last(v-if="chat.last_message && chat.last_message.message")
        .user {{ lastUserName }}
        .msg {{ chat.last_message.message }}
      .contact_count(v-if="(chat.type_id === chatTypes.group || chat.type_id === chatTypes.private || chat.type_id === chatTypes.channel) && unreadMessages") {{ unreadMessages }}
      .contact_menu(v-if="unreadMessages")
        .contact_menu_inner(:id="'menu_' + chat.id")
          v-menu(nudge-bottom="25", :attach="'#menu_' + chat.id")
            template(v-slot:activator="{ on }")
              v-icon(size="26", v-on="on", color="#1F2126") mdi-dots-vertical
            .tooltip
              .tooltip_item(@click.stop="markAllAsRead(chat.id)") {{ $t('actions.markAsRead') }}
</template>

<script>
import AvatarPlaceholder from '@/components/AvatarPlaceholder';
import SvgMsgLogo from '@/components/svg/SvgMsgLogo';
import SvgMsgGroup from '@/components/svg/SvgMsgGroup';
import SvgReferralProp from '@/components/svg/SvgReferralProp';
import { SET_ACTIVE_CHAT } from '@/store/modules/chats/types';
import { getAvatarClass, avatarUrlWithResize } from '@/utils';
import CONSTANTS from '@/constants';
import socket from '@/socket';

export default {
  components: {
    SvgMsgLogo,
    SvgMsgGroup,
    SvgReferralProp,
    AvatarPlaceholder
  },
  methods: {
    onClick() {
      if (!this.active) {
        this.$eventHub.$emit('chatClicked');
      } else {
        return;
      }

      this.$store.commit(`chats/${SET_ACTIVE_CHAT}`, this.active ? null : this.chat);
    },
    cancel() {
      this.$emit('cancel', false);
    },
    markAllAsRead(chatId) {
      socket.emit('read-messages-all', { chat_id: chatId });
    }
  },
  props: {
    chat: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    isNewGroup: { type: Boolean, default: false }
  },
  data() {
    return {
      getAvatarClass,
      avatarUrlWithResize,
      chatTypes: CONSTANTS.chatTypes
    };
  },
  computed: {
    title() {
      switch (this.chat.type_id) {
        case 1:
        case 5:
          return this.chat.title;
        case 4:
          if (this.chat.contact) {
            return `${this.chat.contact.first_name} ${this.chat.contact.last_name}`;
          }
          return 'Private chat';
        default: return this.chat.name;
      }
    },
    unreadMessages() {
      const count = this.$store.state.chats.unreadMessagesCount[this.chat.id] || 0;

      if (count === 1000) return '1K';

      if (count > 1000) {
        const str = count.toString();

        return `${str.charAt(0)}K+`;
      }

      return count;
    },
    activeChat() {
      return this.$store.state.chats.activeChat;
    },
    active() {
      if (!this.activeChat) return false;

      return this.activeChat.id === this.chat.id;
    },
    authUserId() {
      return this.$store.state.user.id;
    },
    lastUserName() {
      if (this.chat.last_message.user_id === this.authUserId) {
        return this.$t('common.you');
      }

      if (this.chat.type_id === this.chatTypes.group) {
        return `${this.chat.last_message.user.first_name} ${this.chat.last_message.user.last_name}`;
      }

      return '';
    },
    lastMessageDate() {
      const now = new Date();
      const date = new Date(this.chat.last_message.created_at);
      const minutes = date.getMinutes();
      const hours = date.getHours();
      const day = date.getDate();

      const diff = now - date;
      const diffHours = Math.floor(diff / 1000 / 60 / 60);
      // if (diffHours < 1) return this.$t('timing.minutesAgo', { number: Math.floor(diff / 1000 / 60) });

      if (diffHours < 24) {
        return day === now.getDate() ? `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}` : this.$t('timing.yesterday');
      }

      return this.$options.filters.contactDate(this.chat.last_message.created_at, this.$i18n.locale);
    }
  }
};
</script>

<style lang="scss" scoped>
  @import '../assets/scss/variables';

  .contact {
    &_item {
      width: 100%;
      max-width: 360px;
      padding: 16px 23px 14px;
      display: flex;
      position: relative;
      cursor: pointer;
      align-items: center;
      border-radius: 6px;

      &.ticked .contact_info__name svg { fill: $orange-3; }

      &.disabled {
        pointer-events: none;
        opacity: .3;
        cursor: default;
      }

      &:hover { background: $light-theme-line; }

      &.hasUnread:hover {
        .contact_count,
        .contact_date {
          opacity: 0;
        }

        .contact_menu { opacity: 1 !important; }
      }

      @media screen and (max-width: 1024px) { max-width: 100%; }

      @media screen and (max-width: 500px) {
        border-bottom: 1px solid $grey-light11;
        padding-left: 10px;
        padding-right: 10px;

        .contact_preview > * {
          width: 35px !important;
          height: 35px !important;
          min-width: 35px !important;
        }

        .name_wrap {
          font-size: 14px !important;

          & > svg {
            width: 12px;
            height: 12px;
          }
        }

        .contact_date { font-size: 10px; }

        .contact_count { top: 18px; }

        ::v-deep .v-avatar span { font-size: 14px !important; }
      }
    }

    &_preview > * {
      width: 50px;
      height: 50px;
      background: $light-theme-line center;
      background-size: cover;
      border-radius: 50%;
      min-width: 50px;
    }

    &_info {
      margin-left: 14px;
      max-width: 250px;
      width: calc(100% - 64px);
      position: relative;

      @media screen and (max-width: 1024px) { max-width: 84%; }

      &__name {
        display: flex;
        align-items: center;
        line-height: normal;
        font-size: 16px;
        color: $sub-black;
        font-weight: 600;
        width: 100%;
        position: relative;
        justify-content: space-between;

        .ref { margin-left: 8px; }

        .name_wrap {
          display: flex;
          align-items: center;
        }

        .name_cancel {
          color: $error-color;
          font-weight: 600;
          font-size: 16px;
        }

        span {
          text-align: left;
          display: block;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          max-width: 160px;
        }

        svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
          margin-right: 9px;
        }
      }

      &__last {
        margin-top: 8px;
        line-height: normal;
        font-size: 12px;
        text-align: left;
        width: 85%;
        color: $light-theme-text;
        font-weight: 600;

        .user,
        .msg {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .user {
          font-weight: 500;
          color: $sub-black;
          margin-bottom: 3px;
        }
      }
    }

    &_date {
      font-size: 12px;
      color: $light-theme-text;
      font-weight: 600;
      white-space: nowrap;
      opacity: 1;
      transition: all .2s $easing;
    }

    &_menu {
      opacity: 0;
      position: absolute;
      right: 0;
      top: calc(50% - 13px);
      transition: all .2s $easing;

      &_inner {
        position: relative;

        ::v-deep .v-menu__content {
          min-width: 150px !important;
          position: absolute;
          top: 45px !important;
          left: calc(50% - 75px) !important;
          border-radius: 15px;
          background: $white;
          padding: 20px 20px 15px;
          overflow: visible;
          contain: none;

          box-shadow: 0 0 6px $light-theme-05, 0 -10px 70px $light-theme-08;

          @media screen and (max-width: 768px) {
            max-width: 200px;
            left: unset !important;
            right: -23px !important;
            top: 35px !important;

            .tooltip::before { content: none !important; }
          }
        }

        .tooltip {
          background: $white;
          position: relative;

          &::before {
            content: '';
            width: 30px;
            height: 30px;
            background: $white;
            border-radius: 6px;
            position: absolute;
            top: -30px;
            transform: rotate(45deg);
            right: 40px;

            @media screen and (max-width: 690px) { right: 20px; }
          }

          &_item {
            color: $blue;
            display: flex;
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 10px;
            cursor: pointer;

            &:last-child { margin-bottom: 0; }

            svg {
              fill: $blue;
              width: 16px;
              height: 16px;
              margin-right: 13px;
              margin-top: 3px;
            }
          }
        }

        ::v-deep .v-icon::after { content: none !important; }

        ::v-deep .v-menu {
          position: absolute;
          top: 0;
          right: 0;
        }
      }
    }
  }

  .special .contact_info__name { color: $orange-3; }

  .contact_count {
    line-height: normal;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    text-align: center;
    color: $white;
    background: $error-color;
    font-weight: 600;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    position: absolute;
    top: 24px;
    right: -2px;
    opacity: 1;
    transition: all .2s $easing;
  }

  .active { background: $light-theme-line; }

  svg.logo { fill: $orange-3; }
</style>
